import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../authentication/firebase";
import { Close, CheckCircle, PersonAdd } from "@mui/icons-material";
import { CircularProgress, Modal, Backdrop, Fade } from "@mui/material";

const WaitlistPopup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (isOpen && auth.currentUser) {
      setFormData(prev => ({
        ...prev,
        email: auth.currentUser.email || "",
        firstName: auth.currentUser.displayName?.split(" ")[0] || "",
        lastName: auth.currentUser.displayName?.split(" ").slice(1).join(" ") || ""
      }));
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
        throw new Error("Please fill in all required fields");
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error("Please enter a valid email address");
      }

      // Add to waitlist collection
      const waitlistData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        waitlistType: "tree-buddy",
        createdAt: serverTimestamp(),
        userId: auth.currentUser?.uid || null,
        status: "pending"
      };

      await addDoc(collection(db, "waitlist"), waitlistData);
      
      setSubmitted(true);
      setLoading(false);
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
        });
        setSubmitted(false);
        onClose();
      }, 2000);

    } catch (err) {
      console.error("Error adding to waitlist:", err);
      setError(err.message || "Failed to join waitlist. Please try again.");
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
      });
      setError(null);
      setSubmitted(false);
      onClose();
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        style: { backgroundColor: "rgba(0, 0, 0, 0.7)" }
      }}
    >
      <Fade in={isOpen}>
        <StyledModal>
          <motion.div
            className="modal-content"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="modal-header">
              <div className="header-content">
                <PersonAdd className="header-icon" />
                <h2>Join the Tree Buddy Waitlist</h2>
              </div>
              <button 
                className="close-button" 
                onClick={handleClose}
                disabled={loading}
              >
                <Close />
              </button>
            </div>

            <div className="modal-body">
              {submitted ? (
                <motion.div 
                  className="success-message"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <CheckCircle className="success-icon" />
                  <h3>You're on the list!</h3>
                  <p>Thank you for joining our Tree Buddy waitlist. We'll notify you when the program launches!</p>
                </motion.div>
              ) : (
                <>
                  <p className="description">
                    All of our tree buddies have found a home! We'll let you know as soon as new Lil' Wroots are ready to grow.
                  </p>
                  
                  <form onSubmit={handleSubmit} className="waitlist-form">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="Enter your first name"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Enter your last name"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter your email address"
                        required
                        disabled={loading}
                      />
                    </div>

                    {error && (
                      <motion.div 
                        className="error-message"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {error}
                      </motion.div>
                    )}

                    <button 
                      type="submit" 
                      className="submit-button"
                      disabled={loading || !formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()}
                    >
                      {loading ? (
                        <>
                          <CircularProgress size={20} color="inherit" />
                          Joining Waitlist...
                        </>
                      ) : (
                        "Join Waitlist"
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </StyledModal>
      </Fade>
    </Modal>
  );
};

const StyledModal = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  outline: none;

  .modal-content {
    background: white;
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    max-width: 500px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 24px 16px 24px;
    border-bottom: 1px solid #e5e7eb;

    .header-content {
      display: flex;
      align-items: center;
      gap: 12px;

      .header-icon {
        color: var(--primary-color);
        font-size: 28px;
      }

      h2 {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 600;
        color: #1f2937;
      }
    }

    .close-button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 8px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.2s;
      color: #6b7280;

      &:hover:not(:disabled) {
        background-color: #f3f4f6;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  .modal-body {
    padding: 24px;

    .description {
      color: #6b7280;
      font-size: 1rem;
      line-height: 1.5;
      margin-bottom: 24px;
      text-align: center;
    }

    .waitlist-form {
      display: flex;
      flex-direction: column;
      gap: 20px;

      .form-group {
        display: flex;
        flex-direction: column;
        gap: 8px;

        label {
          font-weight: 500;
          color: #374151;
          font-size: 0.875rem;
        }

        input {
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.2s, box-shadow 0.2s;
          outline: none;

          &:focus {
            border-color: var(--primary-color);
            box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
          }

          &:disabled {
            background-color: #f9fafb;
            cursor: not-allowed;
          }

          &::placeholder {
            color: #9ca3af;
          }
        }
      }

      .error-message {
        background-color: #fef2f2;
        border: 1px solid #fecaca;
        color: #dc2626;
        padding: 12px 16px;
        border-radius: 8px;
        font-size: 0.875rem;
        text-align: center;
      }

      .submit-button {
        background: linear-gradient(135deg, var(--primary-color), #16a34a);
        color: white;
        border: none;
        padding: 14px 24px;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        margin-top: 8px;

        &:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
        }

        &:active:not(:disabled) {
          transform: translateY(0);
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }
      }
    }

    .success-message {
      text-align: center;
      padding: 20px 0;

      .success-icon {
        color: #10b981;
        font-size: 48px;
        margin-bottom: 16px;
      }

      h3 {
        color: #1f2937;
        font-size: 1.5rem;
        font-weight: 600;
        margin: 0 0 12px 0;
      }

      p {
        color: #6b7280;
        font-size: 1rem;
        line-height: 1.5;
        margin: 0;
      }
    }
  }

  @media (max-width: 640px) {
    padding: 16px;

    .modal-content {
      border-radius: 12px;
    }

    .modal-header {
      padding: 20px 20px 16px 20px;

      .header-content {
        h2 {
          font-size: 1.25rem;
        }
      }
    }

    .modal-body {
      padding: 20px;

      .waitlist-form {
        gap: 16px;

        .form-group {
          input {
            padding: 10px 14px;
            font-size: 0.875rem;
          }
        }

        .submit-button {
          padding: 12px 20px;
          font-size: 0.875rem;
        }
      }
    }
  }
`;

export default WaitlistPopup;
